import { useEffect, useState, type FormEvent } from "react";
import { PageHeader, LoadingState, ErrorState } from "@/components/PageHeader";
import { Button, Input, Label, Select } from "@/components/ui/form";
import { Card, CardBody, DataTable, DataTableHead, DataTableTd, DataTableTh } from "@/components/ui/card";
import { apiFetch } from "@/utils/api";

interface UserRow {
  id: string;
  email: string;
  display_name: string;
  role: string;
  active: number;
}

const emptyEditForm = {
  displayName: "",
  role: "sales",
  active: true,
  password: "",
};

export function AdminUsersPage() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ email: "", displayName: "", role: "sales", password: "" });
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [saveError, setSaveError] = useState("");

  const load = () => apiFetch<{ users: UserRow[] }>("/users").then((d) => setUsers(d.users));

  useEffect(() => {
    load().catch((e) => setError(e instanceof Error ? e.message : "Failed")).finally(() => setLoading(false));
  }, []);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    await apiFetch("/users", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    setShowNew(false);
    setForm({ email: "", displayName: "", role: "sales", password: "" });
    await load();
  };

  const startEdit = (user: UserRow) => {
    setEditingId(user.id);
    setSaveError("");
    setEditForm({
      displayName: user.display_name,
      role: user.role,
      active: !!user.active,
      password: "",
    });
    setShowNew(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(emptyEditForm);
    setSaveError("");
  };

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    setSaveError("");
    try {
      await apiFetch(`/users/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          displayName: editForm.displayName,
          role: editForm.role,
          active: editForm.active,
          ...(editForm.password ? { password: editForm.password } : {}),
        }),
      });
      cancelEdit();
      await load();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save");
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div>
      <PageHeader
        title="Users"
        actions={
          <Button
            onClick={() => {
              setShowNew(!showNew);
              cancelEdit();
            }}
          >
            {showNew ? "Cancel" : "Invite User"}
          </Button>
        }
      />
      {showNew && (
        <Card className="mb-6 max-w-lg">
          <CardBody>
            <form onSubmit={create} className="space-y-4">
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
              <div><Label>Display Name</Label><Input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} /></div>
              <div><Label>Role</Label>
                <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="admin">Admin</option>
                  <option value="sales">Sales</option>
                  <option value="viewer">Viewer</option>
                </Select>
              </div>
              <div><Label>Initial Password (optional)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <Button type="submit">Create User</Button>
            </form>
          </CardBody>
        </Card>
      )}
      {editingId && (
        <Card className="mb-6 max-w-lg">
          <CardBody>
            <form onSubmit={saveEdit} className="space-y-4">
              <p className="text-sm text-argon-secondary">
                Editing {users.find((u) => u.id === editingId)?.email}
              </p>
              <div><Label>Display Name</Label><Input value={editForm.displayName} onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })} required /></div>
              <div><Label>Role</Label>
                <Select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                  <option value="admin">Admin</option>
                  <option value="sales">Sales</option>
                  <option value="viewer">Viewer</option>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm text-argon-dark">
                <input
                  type="checkbox"
                  checked={editForm.active}
                  onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                  className="rounded border-gray-300"
                />
                Active account
              </label>
              <div><Label>New Password (optional)</Label><Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Leave blank to keep current" /></div>
              {saveError && <p className="text-sm text-argon-danger">{saveError}</p>}
              <div className="flex gap-2">
                <Button type="submit">Save Changes</Button>
                <Button type="button" variant="secondary" onClick={cancelEdit}>Cancel</Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}
      <Card>
        <CardBody className="pt-2">
          <DataTable>
            <DataTableHead>
              <DataTableTh>Email</DataTableTh>
              <DataTableTh>Name</DataTableTh>
              <DataTableTh>Role</DataTableTh>
              <DataTableTh>Status</DataTableTh>
              <DataTableTh>Actions</DataTableTh>
            </DataTableHead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-gray-50 hover:bg-gray-50/80">
                  <DataTableTd>{u.email}</DataTableTd>
                  <DataTableTd className="font-semibold">{u.display_name}</DataTableTd>
                  <DataTableTd className="capitalize">{u.role}</DataTableTd>
                  <DataTableTd>{u.active ? "Active" : "Inactive"}</DataTableTd>
                  <DataTableTd>
                    <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(u)}>
                      Edit
                    </Button>
                  </DataTableTd>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </CardBody>
      </Card>
    </div>
  );
}
